import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Skeleton
} from '@mui/material';
import {
  SyncOutlined as SyncIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

export default function SyncOperationsTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch sync status first to initialize correctly
  const {
    data: syncStatusData,
    isLoading: isLoadingSyncStatus,
    refetch: refetchSyncStatus,
    error: syncStatusError
  } = useQuery({
    queryKey: ['pricingSyncStatus', 'pricingSyncOperations'],
    queryFn: async () => {
      try {
        const response = await api.getPricingSyncStatus();
        return response.data.data;
      } catch (error) {
        console.error('Error in price sync status query:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: true,
    refetchInterval: 5000, // Poll every 5 seconds
    enabled: true, // Always enabled
    staleTime: 0, // Always consider data stale to force a refresh
    retry: 3 // Retry failed requests 3 times
  });

  // Initialize based on current sync status - do this after the query is defined
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null // 'whole' or 'updated'
  });

  // Update the sync status whenever data changes
  useEffect(() => {
    if (syncStatusError) {
      console.error('Error fetching sync status:', syncStatusError);
      // Ensure buttons are enabled if there's an error
      setIsSyncInProgress(false);
      return;
    }

    // Check if sync is in progress - explicit check for isRunning property
    const syncRunning = syncStatusData?.isRunning === true;

    // Only update the state if the server status differs from our current state
    if (syncRunning !== isSyncInProgress) {
      setIsSyncInProgress(syncRunning);
    }
  }, [syncStatusData, syncStatusError]); // Remove isSyncInProgress from dependencies

  // Fetch sync settings
  const { data: syncSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['pricingSyncSettings'],
    queryFn: async () => {
      const response = await api.getPricingSyncSettings();
      return response.data.data;
    },
    staleTime: 0
  });

  // Check if all required settings are filled
  const areRequiredSettingsFilled = () => {
    if (!syncSettings) return false;

    const requiredFields = [
      'get_pricing_endpoint',
      'get_product_variant_price_endpoint',
      'query_batch_size',
      'whole_pricing_sync_query',
      'updated_pricing_sync_query',
      'pricing_columns'
    ];

    return requiredFields.every(field => {
      const value = syncSettings[field];
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Start whole customers sync mutation
  const startWholePricingSyncMutation = useMutation({
    mutationFn: async () => {
      // Set sync in progress to true IMMEDIATELY when user clicks the button
      setIsSyncInProgress(true);

      // Use the batch size from settings or fallback to 50
      const batchSize =
        syncSettings?.query_batch_size || 50;

      const response = await api.startWholePricingSync({
        query_batch_size: batchSize,
        sync_type: 'whole'
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Whole price sync started successfully');
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
      // Check sync status immediately after starting
      refetchSyncStatus();
    },
    onError: (error) => {
      // If there was an error, we need to reset the sync status
      setIsSyncInProgress(false);
      toast.error(
        `Failed to start whole price sync: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  });

  // Start updated customers sync mutation
  const startUpdatedPricingSyncMutation = useMutation({
    mutationFn: async () => {
      // Set sync in progress to true IMMEDIATELY when user clicks the button
      setIsSyncInProgress(true);

      // Use the batch size from settings or fallback to 50
      const batchSize =
        syncSettings?.query_batch_size || 50;

      const response = await api.startUpdatedPricingSync({
        query_batch_size: batchSize,
        sync_type: 'today'
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Today's updated price sync started successfully");
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
      // Check sync status immediately after starting
      refetchSyncStatus();
    },
    onError: (error) => {
      // If there was an error, we need to reset the sync status
      setIsSyncInProgress(false);
      toast.error(
        `Failed to start updated price sync: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  });

  return (
    <>
      <Grid container spacing={3}>
        {isLoadingSyncStatus || isLoadingSettings ? (
          // Detailed skeletons that match the card structure
          <>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      height={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={150} />
                  </Box>
                  <Skeleton variant='text' width='90%' />
                  <Skeleton variant='text' width='100%' />

                  <Skeleton
                    variant='rectangular'
                    sx={{
                      mt: 2,
                      mb: 2,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: 'rgba(41, 182, 246, 0.1)'
                    }}
                  />

                  <Skeleton
                    variant='rectangular'
                    height={36}
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      opacity: 0.2
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <Skeleton
                      variant='circular'
                      width={24}
                      height={24}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton variant='text' width={150} />
                  </Box>
                  <Skeleton variant='text' width='90%' />
                  <Skeleton variant='text' width='100%' />

                  <Skeleton
                    variant='rectangular'
                    sx={{
                      mt: 2,
                      mb: 2,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: 'rgba(41, 182, 246, 0.1)'
                    }}
                  />

                  <Skeleton
                    variant='rectangular'
                    height={36}
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      opacity: 0.2
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box mt={2} display='flex' justifyContent='center' sx={{ pb: { xs: '50px'} }}>
                <Skeleton
                  variant='rectangular'
                  width={180}
                  height={36}
                  animation='wave'
                  sx={{ borderRadius: 1, bgcolor: 'secondary.light', opacity: 0.3 }}
                />
              </Box>
            </Grid>
          </>
        ) : (
          // Actual content when not loading
          <>
            {/* Sync in progress warning banner */}
            {isSyncInProgress && (
              <Grid item xs={12}>
                <Alert severity='warning' sx={{ mb: 0 }}>
                  Pricing synchronization is currently in progress. Sync operations are disabled until the sync completes.
                </Alert>
              </Grid>
            )}

            {!areRequiredSettingsFilled() && (
              <Grid item xs={12}>
                <Alert
                  severity='warning'
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={() => navigate('/pricing-sync/settings')}
                    >
                    Configure Settings
                    </Button>
                  }
                >
                  <Typography variant='subtitle2' gutterBottom>
                  Configuration Required
                  </Typography>
                  <Typography variant='body2'>
                  Please configure all required settings before running sync operations.
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <SyncIcon color='primary' sx={{ mr: 1 }} />
                    <Typography variant='h6'>Whole Price Sync</Typography>
                  </Box>
                  <Typography variant='body2' color='text.secondary' paragraph>
                    Sync all eligible price from your ERP system to Shopify.
                    This operation will create, update, or skip price based on
                    their existence in Shopify.
                  </Typography>
                  <Alert severity='info' sx={{ mb: 2, fontSize: { xs: '12px'} }}>
                    This operation may take a long time depending on the number of products in your ERP system.
                    You can check the sync progress in the Sync Logs section.
                  </Alert>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={
                      startWholePricingSyncMutation.isPending ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : (
                        <SyncIcon />
                      )
                    }
                    onClick={() => setConfirmDialog({ open: true, type: 'whole' })}
                    disabled={
                      startWholePricingSyncMutation.isPending || isSyncInProgress || !areRequiredSettingsFilled()
                    }
                    fullWidth
                    sx={{
                      width: '100% !important'
                    }}
                  >
                    {startWholePricingSyncMutation.isPending ? 'Starting Sync...' :
                      isSyncInProgress ?
                        (syncStatusData?.activeSyncs?.[0]?.sync_sub_type === 'whole_prices' ?
                          'Whole Price Sync In Progress...' :
                          'Sync In Progress...') :
                        'Start Whole Price Sync'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <RefreshIcon color='primary' sx={{ mr: 1 }} />
                    <Typography variant='h6'>
                      Today's Updated Price
                    </Typography>
                  </Box>
                  <Typography variant='body2' color='text.secondary' paragraph>
                    Sync only the price that have been updated in your ERP
                    system today. This is a faster operation for regular updates.
                  </Typography>
                  <Alert severity='info' sx={{ mb: 2, fontSize: { xs: '12px'} }}>
                    This operation only syncs price that have been modified or
                    created today in your ERP system. You can check the sync
                    progress in the Sync Logs section.
                  </Alert>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={
                      startUpdatedPricingSyncMutation.isPending ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                    onClick={() => setConfirmDialog({ open: true, type: 'updated' })}
                    disabled={
                      startUpdatedPricingSyncMutation.isPending ||
                      isSyncInProgress || !areRequiredSettingsFilled()
                    }
                    fullWidth
                    sx={{
                      width: '100% !important'
                    }}
                  >
                    {startUpdatedPricingSyncMutation.isPending ? 'Starting Sync...' :
                      isSyncInProgress ?
                        (syncStatusData?.activeSyncs?.[0]?.sync_sub_type === 'today_prices' ?
                          'Updated Price Sync In Progress...' :
                          'Sync In Progress...') :
                        'Start Today\'s Updated Price Sync'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box mt={2} display='flex' justifyContent='center' sx={{ pb: { xs: '50px'} }}>
                <Button
                  variant='contained'
                  color='secondary'
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/sync-logs?syncType=price&direction=erp_to_shopify')}
                >
                  View Sync Logs
                </Button>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.type === 'whole' ? 'Start Whole Price Sync?' : 'Start Today\'s Updated Price Sync?'}
        message={confirmDialog.type === 'whole'
          ? 'Are you sure you want to start a whole price sync? This may take a long time depending on the number of price records.'
          : 'Are you sure you want to start today\'s updated price sync? This will only sync price records updated or created today.'}
        confirmText={confirmDialog.type === 'whole' ? 'Start Whole Sync' : 'Start Updated Sync'}
        cancelText='Cancel'
        confirmColor='primary'
        isLoading={
          (confirmDialog.type === 'whole' && startWholePricingSyncMutation.isPending) ||
          (confirmDialog.type === 'updated' && startUpdatedPricingSyncMutation.isPending)
        }
        onCancel={() => setConfirmDialog({ open: false, type: null })}
        onConfirm={() => {
          if (confirmDialog.type === 'whole') {
            startWholePricingSyncMutation.mutate(undefined, {
              onSettled: () => setConfirmDialog({ open: false, type: null })
            });
          } else if (confirmDialog.type === 'updated') {
            startUpdatedPricingSyncMutation.mutate(undefined, {
              onSettled: () => setConfirmDialog({ open: false, type: null })
            });
          }
        }}
      />
    </>
  );
}
